import { useState } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { ShippingOptionType } from '../../../bff-api/baskets/model';
import { StoresInventoryStatus } from '../../../bff-api/stores/model';
import { BopisStoreSearchDrawer } from '../../../components/bopis-store-search-drawer';
import { SDDStoreSearchDrawer } from '../../../components/sdd-store-search-drawer';
import { ZipCodeModal } from '../../../components/zip-code-modal';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { FREE_SHIPPING_LABEL, useIsFreeShipping } from '../../../hooks/useIsFreeShipping';
import { useSameDayDelivery } from '../../../hooks/useSameDayDelivery';
import { useUserZipCode } from '../../../hooks/useUserZipCode';
import { SelectButton } from '../../../ui-components/button/select-button';
import { getShippingOptions } from '../../../utils/product-utils';
import { BopisBox } from './bopis-box';
import { SameDayDeliveryBox } from './same-day-delivery-box';
import { ShipToBox } from './ship-to-box';
import { useDeliveryOptionDescriptions } from './useDeliveryOptionDescriptions';
import type { ShippingOption } from '../../../bff-api/baskets/model';
import type { Inventory } from '../../../contexts/utils/types';

const isShippingOptionsDisabled = (
    isLoadingInventory: boolean,
    inventory: Inventory,
    option?: ShippingOption,
): boolean =>
    !isLoadingInventory &&
    ((option && !option.enabled) ||
        inventory.status === StoresInventoryStatus.PRODUCT_NOT_ELIGIBLE);

interface Props {
    id: string;
    className?: string;
}

export const PDPDeliveryOptions: React.FC<Props> = ({ id = '', className }: Props) => {
    const intl = useIntl();
    const descriptions = useDeliveryOptionDescriptions();
    const { data: zipCode = '' } = useUserZipCode();

    const {
        isOpen: isOpenZipCode,
        onOpen: onOpenZipCode,
        onClose: onCloseZipCode,
    } = useDisclosure();
    const {
        isOpen: isOpenStoreSearch,
        onOpen: onOpenStoreSearch,
        onClose: onCloseStoreSearch,
    } = useDisclosure();
    const {
        isOpen: isOpenLocationSearch,
        onOpen: onOpenLocationSearch,
        onClose: onCloseLocationSearch,
    } = useDisclosure();
    const {
        product,
        selectedQuantity,
        isLoadingProductInventory,
        isLoadingBopisInventory,
        isLoadingSDDInventory,
        isFetchingCurrentInventory,
        inventoryStore: { inventories },
        selectedShippingOptionType,
        setSelectedShippingOptionType,
    } = usePDP();
    const isFreeShipping = useIsFreeShipping();

    const [localSelectedShippingOptionType, setLocalSelectedShippingOptionType] =
        useState<ShippingOptionType>(selectedShippingOptionType);

    const onClick = (newSelectedShippingOptionType: ShippingOptionType): void => {
        setLocalSelectedShippingOptionType(newSelectedShippingOptionType);
        setTimeout(() => {
            setSelectedShippingOptionType(newSelectedShippingOptionType);
        }, 0);
    };

    const bopisInventory = inventories[ShippingOptionType.BOPIS];
    const sddInventory = inventories[ShippingOptionType.SDD];

    const { description: sameDayDeliveryTitle } = useSameDayDelivery();

    if (!product) {
        return null;
    }

    const { shipToOption, bopisOption, sddOption } = getShippingOptions(product);

    const isShipToSelected =
        shipToOption?.enabled && localSelectedShippingOptionType === shipToOption.type;
    const isBopisSelected =
        bopisOption?.enabled && localSelectedShippingOptionType === bopisOption.type;
    const isSDDSelected = sddOption?.enabled && localSelectedShippingOptionType === sddOption.type;

    const isBopisDisabled = isShippingOptionsDisabled(
        isLoadingBopisInventory,
        bopisInventory,
        bopisOption,
    );
    const isSddDisabled = isShippingOptionsDisabled(isLoadingSDDInventory, sddInventory, sddOption);

    const shippingPrice = isFreeShipping(product, selectedQuantity)
        ? FREE_SHIPPING_LABEL
        : shipToOption?.price;

    return (
        <Flex data-testid={id} direction="column" className={`space-y-2 ${className}`}>
            <Flex
                className="xs:max-sm:space-y-2 sm:max-md:space-x-2 md:max-lg:space-y-2 lg:space-x-2"
                wrap={{
                    base: 'wrap',
                    sm: 'nowrap',
                    md: 'wrap',
                    lg: 'nowrap',
                }}
            >
                {shipToOption ? (
                    <>
                        <SelectButton
                            id={`${id}-${shipToOption.type}`}
                            key={shipToOption.type}
                            isDisabled={!shipToOption.enabled || isLoadingProductInventory}
                            value={shipToOption.type}
                            price={shippingPrice}
                            title={intl.formatMessage({
                                id: 'delivery.options.shipping',
                                defaultMessage: 'Shipping',
                            })}
                            description={descriptions[shipToOption.type]}
                            icon={shipToOption.type}
                            onClick={(value) => onClick(value as ShippingOptionType)}
                            isActive={localSelectedShippingOptionType === shipToOption.type}
                        />

                        {isShipToSelected ? (
                            <ShipToBox
                                id={`${id}-edd`}
                                product={product}
                                onClickZipCode={onOpenZipCode}
                                h="fit-content"
                                w="100%"
                                display={{ base: 'flex', sm: 'none', md: 'flex', lg: 'none' }}
                            />
                        ) : null}
                    </>
                ) : null}

                {bopisOption ? (
                    <>
                        <SelectButton
                            id={`${id}-${bopisOption.type}`}
                            key={bopisOption.type}
                            isDisabled={isBopisDisabled || isLoadingBopisInventory}
                            value={bopisOption.type}
                            price={bopisOption.price}
                            title={intl.formatMessage({
                                id: 'delivery.options.store.pickup',
                                defaultMessage: 'Store Pickup',
                            })}
                            description={descriptions[bopisOption.type]}
                            icon={bopisOption.type}
                            onClick={(value) => onClick(value as ShippingOptionType)}
                            isActive={localSelectedShippingOptionType === bopisOption.type}
                        />

                        {isBopisSelected ? (
                            <Box
                                h="fit-content"
                                w="100%"
                                display={{ base: 'flex', sm: 'none', md: 'flex', lg: 'none' }}
                            >
                                <BopisBox id={`${id}-bopis`} onClickLocation={onOpenStoreSearch} />
                            </Box>
                        ) : null}
                    </>
                ) : null}

                {sddOption ? (
                    <>
                        <SelectButton
                            id={`${id}-${sddOption.type}`}
                            key={sddOption.type}
                            isDisabled={isSddDisabled || isLoadingSDDInventory}
                            value={sddOption.type}
                            price={sddOption.price}
                            title={sameDayDeliveryTitle}
                            description={descriptions[sddOption.type]}
                            icon={sddOption.type}
                            onClick={(value) => onClick(value as ShippingOptionType)}
                            isActive={localSelectedShippingOptionType === sddOption.type}
                        />

                        {isSDDSelected ? (
                            <Box
                                h="fit-content"
                                w="100%"
                                display={{ base: 'flex', sm: 'none', md: 'flex', lg: 'none' }}
                            >
                                <SameDayDeliveryBox
                                    id={`${id}-sdd`}
                                    onClickLocation={onOpenLocationSearch}
                                />
                            </Box>
                        ) : null}
                    </>
                ) : null}
            </Flex>

            {isShipToSelected ? (
                <ShipToBox
                    id={`${id}-edd`}
                    product={product}
                    onClickZipCode={onOpenZipCode}
                    h="fit-content"
                    w="100%"
                    display={{ base: 'none', sm: 'flex', md: 'none', lg: 'flex' }}
                />
            ) : null}

            {isBopisSelected ? (
                <Box
                    h="fit-content"
                    w="100%"
                    display={{ base: 'none', sm: 'flex', md: 'none', lg: 'flex' }}
                >
                    <BopisBox id={`${id}-bopis`} onClickLocation={onOpenStoreSearch} />
                </Box>
            ) : null}

            {isSDDSelected ? (
                <Box
                    h="fit-content"
                    w="100%"
                    display={{ base: 'none', sm: 'flex', md: 'none', lg: 'flex' }}
                >
                    <SameDayDeliveryBox id={`${id}-sdd`} onClickLocation={onOpenLocationSearch} />
                </Box>
            ) : null}

            <ZipCodeModal
                id={`${id}-zip-code-modal`}
                zipCode={zipCode}
                isOpen={isOpenZipCode}
                onClose={onCloseZipCode}
            />

            <BopisStoreSearchDrawer
                id={`${id}-store-search-drawer`}
                isOpen={isOpenStoreSearch}
                onClose={onCloseStoreSearch}
                productId={product.id}
                isLoading={isFetchingCurrentInventory}
            />

            <SDDStoreSearchDrawer
                id={`${id}-location-search-drawer`}
                productId={product.id}
                isOpen={isOpenLocationSearch}
                onClose={onCloseLocationSearch}
                isLoading={isFetchingCurrentInventory}
            />
        </Flex>
    );
};

PDPDeliveryOptions.defaultProps = {
    className: '',
};
